// Plan-starter -> Logger draft hydration. Pure (no React, no component
// state). Split out of workoutMapping.ts (Track R) — distinct seam from
// saved-workout hydration: the input is a PlanStarter (prescribed sets),
// not a performed Workout, so completion/notes/rpe semantics differ
// (everything starts blank, columns seed from the prescription).
import {
  catalogBySlug,
  fromCanonicalKg,
  rirToRpe,
  roundLoadable,
  rpeToRir,
  type EffortScale,
  type Exercise,
  type MovementPattern,
  type PlanStarter,
} from '@jackedlogs/shared';
import type {
  DraftEntry,
  LoggerDraft,
  WeightUnit,
} from './state/loggerDraftTypes';
import { catalogLoadedSides, deriveEntryKind } from './deriveEntryKind';

// Builds a logger draft from a plan starter (buildPlanStarter output).
// Mirrors hydrateFromWorkout: weighted sets get the resolved kg as
// the visible weight, bodyweight entries surface the prescribed extra
// (signed). The plan's fromPlanId/name/notes pass through unchanged so
// the save payload carries them to the API.
//
// catalog provides the isBodyweight flag per slug. When the catalog is
// missing the slug (rare — drafts created against an out-of-date
// snapshot), we fall back to weighted; the user can still log the
// session, the worst case is a misrendered set-row label.
export function hydrateFromPlanStarter(
  starter: PlanStarter,
  unit: WeightUnit,
  catalog: ReadonlyArray<Exercise>,
  effortScale: EffortScale,
  catalogPatterns: Record<string, MovementPattern | null> = {},
  // Finest loadable kg increment for the athlete's plates (device-local,
  // from plateConfig.loadRoundingStepKg). Only the %1RM-derived seed is
  // floored to it (LG-15); omit → roundLoadable's 2.5 kg default.
  // TODO(cleanup): 6 positional params (2 optional) is getting fragile —
  // fold catalogPatterns + roundingStepKg into an options object next time
  // this signature is touched, so call-sites stop passing placeholder undefined.
  roundingStepKg?: number,
): LoggerDraft {
  const bySlug = catalogBySlug(catalog);

  // Pre-fill the effort input with the coach's prescribed target, in the
  // athlete's preferred scale. The program authors in one scale (rpe OR
  // rir); we normalise to canonical RIR then project to effortScale, and
  // pre-fill only the matching field (the save path derives the mirror).
  const effortStrings = (s: {
    rpe?: number;
    rir?: number;
  }): { rpe: string; rir: string } => {
    let canonicalRir: number | undefined;
    if (s.rir !== undefined && Number.isFinite(s.rir)) canonicalRir = s.rir;
    else if (s.rpe !== undefined && Number.isFinite(s.rpe))
      canonicalRir = rpeToRir(s.rpe);
    if (canonicalRir === undefined) return { rpe: '', rir: '' };
    return effortScale === 'rir'
      ? { rpe: '', rir: String(canonicalRir) }
      : { rpe: String(rirToRpe(canonicalRir)), rir: '' };
  };

  const entries: DraftEntry[] = starter.entries.map((e) => {
    const ex = bySlug.get(e.exerciseId);
    // Same catalog→kind rule as add + swap (deriveEntryKind), but the plan
    // starter may hit a missing catalog row, so it pre-resolves movementPattern
    // with its own fallback and passes the parts.
    const { isBodyweight, isCardio, isTimeBased, movementPattern } =
      deriveEntryKind({
        isBodyweight: ex?.isBodyweight,
        movementPattern:
          ex?.movementPattern ?? catalogPatterns[e.exerciseId] ?? null,
        isTimeBased: ex?.isTimeBased,
      });
    // Laterality multiplier seeded from the catalog via the shared seam
    // (catalogLoadedSides — same rule as add + swap, incl. the cardio
    // guard this copy used to lack). The entry-header chip overrides.
    // Kept on one variable so the entry and its weighted sets stay
    // consistent.
    const lateralSides: 1 | 2 = catalogLoadedSides({
      movementPattern: ex?.movementPattern ?? catalogPatterns[e.exerciseId] ?? null,
      unilateral: ex?.unilateral,
    });
    // Plan-driven effort column: ON iff the coach prescribed effort
    // somewhere on this entry — per-set rpe/rir target OR per-entry
    // rpeEnabled/rirEnabled opt-in flag. Cardio entries never get the
    // column. No prescription = column off; the athlete opts in per
    // entry via the chip. Decoupled from prefill (effortStrings above):
    // an opt-in flag with no per-set target yields a visible-but-empty
    // column. Replaces the v2.39.0 default-on-for-everything rule that
    // ignored Slot.rirEnabled/rpeEnabled (inbox dd7de4f8, 2026-05-28).
    const hasPerSetEffortTarget = e.sets.some(
      (s) => s.rpe !== undefined || s.rir !== undefined,
    );
    const effortColumn =
      !isCardio &&
      !isTimeBased &&
      (e.rpeEnabled === true ||
        e.rirEnabled === true ||
        hasPerSetEffortTarget);
    return {
      exerciseId: e.exerciseId,
      // DraftEntry.exerciseName is a snapshot string; resolve to EN
      // canonical here so the draft serialises predictably. The UI
      // re-localises live wherever it renders from a live catalog row.
      exerciseName: ex?.name.en ?? e.exerciseId,
      isBodyweight,
      isCardio,
      isTimeBased,
      movementPattern,
      group: e.group ?? null,
      // EQ-1: a plan prescribes the EXERCISE, never the machine — the coach
      // cannot know which unit the athlete will stand in front of. So a
      // plan-started entry begins with no instance; the add-time silent
      // resolution and replace are what fill it in.
      equipmentInstance: null,
      location: null,
      loadedSides: lateralSides,
      // Plan cues (💬 instruction + 🎯 per-set) are NOT copied into the draft
      // (note-model consolidation): they're display-only, re-resolved per-slug
      // by usePlanCues, so an in-workout swap can't inherit the old exercise's.
      effortColumn,
      historyOpen: false,
      sets: e.sets.map((s) => {
        // The plan's AMRAP lands on the PRESCRIPTION half only. It draws
        // the read-only badge; it must never be mistaken for what the
        // athlete actually did, so the outcome half starts false on every
        // freshly started set and stays false until the athlete says
        // otherwise with the chip (measurement-contract §5: what you
        // declare serves to propose, never to measure).
        const amrapPrescribed = s.amrap === true;
        // ...and on an AMRAP the reps CELL is the prescription's last way
        // in, so it closes here (LG-28). Every ordinary set seeds its
        // prescribed reps into the cell, which is a helpful default: the
        // plan asked for 10, 10 is almost certainly what you did. On an
        // AMRAP the same seed is a trap — the number in the box is the
        // floor, and an athlete who reads it as the answer stops there.
        // That is exactly what happened in the smoke of 2026-07-24: sets
        // prescribed AMRAP, performed at the prescribed reps, then
        // confirmed "to failure" — a measurement fabricated out of a
        // request ([[feedback_prescription_never_becomes_measurement]]).
        // Empty cell + floor in the placeholder: the athlete must type
        // what they actually did, and there is nothing to accept by
        // doing nothing.
        const reps = amrapPrescribed ? '' : String(s.reps);
        const amrapFloorField = amrapPrescribed
          ? { amrapFloor: String(s.reps) }
          : {};
        const { rpe, rir } = effortStrings(s);
        // weightSource gates the prefilled cell (Phase 1, default-weight
        // chain). Absent → 'blank' from resolveDefaultWeight → empty
        // cell; otherwise stamp the chain's source onto the DraftSet so
        // the save path can persist provenance + an athlete edit can
        // flip to 'manual'. Bodyweight keeps its own pure-bw render gate
        // (kg === 0) since "no prescription" on a bodyweight exercise
        // means a pure-bw rep, not a blank cell.
        const sourceField = s.weightSource
          ? { weightSource: s.weightSource }
          : {};
        if (isBodyweight) {
          // Empty extra means "pure bw". Plan-prescribed 0 also reads
          // as "pure bw" in the logger — the athlete can add load with
          // the per-row chip if the plan was wrong, no surprise.
          if (s.weightKg === 0) {
            return {
              reps,
              weight: '',
              unit,
              extraNegative: false,
              completedAt: null,
              fail: false,
              prFlash: false,
              note: '',
              rpe,
              rir,
              loadedSides: lateralSides,
              duration: '',
              distanceM: '',
              avgHrBpm: '',
              calories: '',
              amrapPrescribed,
              ...amrapFloorField,
              amrap: false,
              dropset: null,
              ...sourceField,
            };
          }
          const extraNegative = s.weightKg < 0;
          const displayed = fromCanonicalKg(Math.abs(s.weightKg), unit);
          return {
            reps,
            weight: String(displayed),
            unit,
            extraNegative,
            completedAt: null,
            fail: false,
            prFlash: false,
            note: '',
            rpe,
            rir,
            loadedSides: lateralSides,
            duration: '',
            distanceM: '',
            avgHrBpm: '',
            calories: '',
            amrapPrescribed,
            ...amrapFloorField,
            amrap: false,
            dropset: null,
            ...sourceField,
          };
        }
        // %1RM-derived seeds resolve to un-loadable numbers (e.g. 68.38 kg,
        // LG-15) — floor to the athlete's finest plate increment so the
        // seeded cell shows a weight they can build. Display/seed only; the
        // plan's saved % target is untouched. Other sources (prescribed
        // absolute, history, manual) already came from a real scale/entry.
        const seededKg =
          s.weightSource === 'computed-percent'
            ? roundLoadable(s.weightKg, unit, { stepKg: roundingStepKg })
            : s.weightKg;
        const displayed =
          s.weightSource !== undefined
            ? String(fromCanonicalKg(seededKg, unit))
            : '';
        return {
          reps,
          weight: displayed,
          unit,
          extraNegative: false,
          completedAt: null,
          fail: false,
          prFlash: false,
          note: '',
          rpe,
          rir,
          loadedSides: lateralSides,
          duration: '',
          distanceM: '',
          avgHrBpm: '',
          calories: '',
          amrapPrescribed,
          ...amrapFloorField,
          amrap: false,
          dropset: null,
          ...sourceField,
        };
      }),
    };
  });

  // startedAtLocal is datetime-local format (YYYY-MM-DDTHH:mm). The
  // draft holds an ISO datetime so we let the device clock fill in
  // the seconds and timezone offset — the local intent is preserved
  // (same minute), the canonical timestamp is honest about when the
  // session actually starts.
  const startedAt = new Date().toISOString();

  return {
    startedAt,
    entries,
    ...(starter.planId ? { fromPlanId: starter.planId } : {}),
    ...(starter.name ? { name: starter.name } : {}),
    ...(starter.notes ? { notes: starter.notes } : {}),
    ...(starter.coachNotes ? { coachNotes: starter.coachNotes } : {}),
  };
}
